import React from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, MessageCircle } from "lucide-react";
import TiltCard from "./TiltCard";
import { DESTINATIONS, BRAND } from "../data/site";

const buildWaLink = (place) => {
  const text = encodeURIComponent(
    `Hi Happy Journey 4U, I'd love to know more about your ${place} package. Could you share details and pricing?`
  );
  return `https://wa.me/${BRAND.phones[0].raw}?text=${text}`;
};

export default function Destinations() {
  return (
    <section
      id="destinations"
      data-testid="destinations-section"
      className="relative py-24 sm:py-32 bg-slate-950 overflow-hidden"
    >
      {/* Background warmth */}
      <div className="absolute inset-0 -z-0">
        <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-brand-orange/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-0 left-0 w-[40rem] h-[40rem] bg-brand-ocean/20 blur-[120px] rounded-full" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.24em] text-brand-orange font-semibold">
              Popular Destinations
            </div>
            <h2
              data-testid="destinations-heading"
              className="mt-3 font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight text-balance"
            >
              Where would you like to{" "}
              <span className="italic text-brand-orange">wake up</span> next?
            </h2>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-white/70 max-w-md leading-relaxed text-sm">
              Hand-picked itineraries our travellers love. Tap any destination
              to chat with us on WhatsApp for the latest pricing.
            </p>
            <div className="inline-flex items-center gap-1.5 text-[#25D366] text-xs font-medium">
              <MessageCircle className="w-3.5 h-3.5" />
              All cards open WhatsApp directly
            </div>
          </div>
        </div>

        {/* Bento grid */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-6 auto-rows-[260px]">
          {DESTINATIONS.map((d, i) => {
            const span =
              i === 0
                ? "col-span-2 row-span-2"
                : i === 4
                  ? "lg:col-span-2 row-span-1"
                  : "col-span-1 row-span-1";
            return (
              <motion.a
                key={d.name}
                href={buildWaLink(d.name)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`destination-card-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.7,
                  delay: i * 0.05,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className={`${span} group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 rounded-3xl`}
                title={`Enquire on WhatsApp about ${d.name}`}
              >
                <TiltCard intensity={9} className="h-full w-full">
                  <div
                    className="relative h-full w-full rounded-3xl overflow-hidden ring-1 ring-white/15 shadow-2xl"
                    style={{ transformStyle: "preserve-3d" }}
                  >
                    <img
                      src={d.image}
                      alt={d.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                    <div className="absolute inset-0 ring-0 group-hover:ring-2 group-hover:ring-brand-orange/60 transition-all duration-500 rounded-3xl" />

                    {/* Tag — consistent position */}
                    <span
                      className="absolute top-4 left-4 px-3 py-1 rounded-full glass text-[0.7rem] tracking-[0.16em] uppercase text-slate-900 font-semibold"
                      style={{ transform: "translateZ(40px)" }}
                    >
                      {d.tag}
                    </span>

                    {/* WhatsApp hover helper — shows on hover for desktop */}
                    <span
                      className="absolute top-4 right-4 hidden group-hover:flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#25D366]/90 text-white text-[0.65rem] font-semibold transition-all"
                      style={{ transform: "translateZ(40px)" }}
                    >
                      <MessageCircle className="w-3 h-3" />
                      Opens WhatsApp
                    </span>

                    {/* Content — consistent layout in every card */}
                    <div
                      className="absolute bottom-0 left-0 right-0 p-5 md:p-6 text-white"
                      style={{ transform: "translateZ(60px)" }}
                    >
                      <div className="flex items-center gap-2 text-white/75 text-xs">
                        <MapPin className="w-3.5 h-3.5" />
                        {d.country}
                      </div>
                      <h3 className="mt-1 font-playfair text-2xl md:text-3xl font-bold leading-none">
                        {d.name}
                      </h3>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="inline-flex items-center gap-1.5 text-sm text-white/80">
                          <Calendar className="w-4 h-4" />
                          {d.duration}
                        </span>
                        {/* Enquire CTA — visible always on mobile, on hover for desktop */}
                        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#25D366] sm:opacity-0 sm:-translate-x-2 sm:group-hover:opacity-100 sm:group-hover:translate-x-0 transition-all duration-500">
                          <MessageCircle className="w-4 h-4" />
                          Enquire
                        </span>
                      </div>
                    </div>
                  </div>
                </TiltCard>
              </motion.a>
            );
          })}
        </div>

        {/* Custom plan CTA — accented differently */}
        <div className="mt-14 flex justify-center">
          <a
            href={`https://wa.me/${BRAND.phones[0].raw}?text=${encodeURIComponent(
              "Hi! I'd like a custom destination plan from Happy Journey 4U."
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="custom-trip-cta"
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-full border-2 border-brand-orange/60 text-brand-orange font-semibold hover:bg-brand-orange hover:text-white hover:border-brand-orange transition-all duration-300 shadow-[0_0_0_0_rgba(243,146,0,0)] hover:shadow-[0_12px_40px_rgba(243,146,0,0.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
          >
            <span className="text-2xl">✨</span>
            <span>Don't see your dream destination? Build a custom plan</span>
            <MessageCircle className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
          </a>
        </div>
      </div>
    </section>
  );
}
